/**
 * The docs' page on trust: what the escrow contract guarantees without anyone's word, what
 * Morcast is trusted for, and the risks that remain with what limits each. It states limits
 * plainly, because a reader who locks a budget or works for a payout decides on them. The
 * statements follow the V1 specification (docs/specification.md, sections 2, 5 and 10).
 */

import { styles } from "../../components/ui.js";
import { DocPage, DocSection, DocTable, Points } from "./parts.js";

/** Where X's rule of January 2026 on apps that reward posting is reported. */
const X_RULE_SOURCE =
  "https://decrypt.co/354736/elon-musks-x-bans-access-infofi-crypto-projects-ai-slop-backlash";

export function Trust() {
  return (
    <DocPage
      group="Morcast"
      title="Trust"
      lead="Nobody has to trust Morcast with money. Morcast is trusted to measure and to judge, and its work is published so that anyone can check it."
    >
      <DocSection id="guarantees" title="What the contract guarantees">
        <Points
          items={[
            {
              text: "A campaign's money can go to three places only, which are the creators' payouts, Morcast's fee and the brand's own wallet.",
            },
            {
              text: "The fee is one fifth of what the campaign spends, and nothing if nothing is recognized.",
            },
            {
              text: "If Morcast does not settle within ten days of the end, the brand withdraws its whole budget.",
            },
            {
              text: "Payouts never expire, and a claim needs nothing from Morcast except the published proof.",
            },
            {
              text: "The contract's code cannot be upgraded or changed.",
            },
          ]}
        />
      </DocSection>

      <DocSection id="trusted-for" title="What Morcast is trusted for">
        <Points
          items={[
            {
              lead: "Measurement.",
              text: "Morcast is the single verifier. It reports the reach the platforms return. YouTube's analytics are visible only to the channel's owner, so each creator can check their own numbers, and others rely on the published evidence.",
            },
            {
              lead: "Judgment.",
              text: "Models and people decide whether content follows the brief. The models, the prompts and each verdict's evidence are published.",
            },
            {
              lead: "Settling the published result.",
              text: "The contract does not check a result itself. It records the hash of the result Morcast settles. If that differed from the published result, anyone could prove it, but the contract could not undo it.",
            },
            {
              lead: "Reading, never posting.",
              text: "Morcast's access to a creator's account is read-only.",
            },
          ]}
        />
      </DocSection>

      <DocSection id="risks" title="Risks that remain">
        <DocTable
          caption="The risks that remain and what limits each"
          head={["Risk", "What limits it"]}
          rows={[
            [
              "A wrong result",
              "Publication before settlement, a review with issues, and public recomputation",
            ],
            [
              "Morcast stops operating",
              "The refund from Day 10, and claims that work from public proofs",
            ],
            [
              "A flaw in the contract",
              "A small contract with open code and tests. It has not had an external audit yet",
            ],
            [
              "Fake reach",
              "Organic reach only, the platform's own adjustments for invalid traffic, the baseline check and the threshold",
            ],
            [
              "A platform changes its rules",
              <>
                Morcast reads reach under the API terms of X and YouTube. In January 2026, X barred
                apps that reward users for posting (
                <a href={X_RULE_SOURCE} rel="noopener" className={styles.link}>
                  Decrypt
                </a>
                ). Campaigns on X depend on X accepting Morcast's model
              </>,
            ],
          ]}
        />
      </DocSection>
    </DocPage>
  );
}
